/**
 * IBP Template Configuration
 * 
 * This file defines the configurable template structure for the GDO bulk upload feature.
 * It can be extended for different business streams by creating similar configuration objects.
 * 
 * Each column definition includes:
 * - key: Unique identifier for the column
 * - label: Header text displayed on the Excel sheet
 * - comment: Static or dynamic comment/hint shown on hover
 * - scope: Whether the attribute is program-level, project-level, or job-level
 * - required: Whether the field is mandatory
 * - width: Column width in characters for Excel
 * - validation: Validation function or configuration
 * - sampleValues: Sample values for the template
 */

export type ColumnScope = 'program' | 'project' | 'job' | 'system';

export interface ValidationRule {
  type: 'required' | 'pattern' | 'enum' | 'custom' | 'dependent' | 'reference';
  pattern?: RegExp;
  allowedValues?: string[] | ((refData: any) => string[]);
  message: string;
  dependsOn?: string[];
  referenceType?: 'sectors' | 'markets' | 'reports' | 'existingGDONames';
  maxLength?: number;
  customValidator?: (value: string, row: Record<string, any>, refData?: any) => string | null;
}

export interface ColumnDefinition {
  key: string;
  label: string;
  comment: string | ((refData: any) => string);
  scope: ColumnScope;
  required: boolean;
  width: number;
  validation: ValidationRule[];
  sampleValues?: string[];
  defaultValue?: string;
  format?: string;
  description?: string;
  example?: string;
}

export interface TemplateConfig {
  id: string;
  name: string;
  description: string;
  businessStream: string;
  fileName: string;
  sheetName: string;
  columns: ColumnDefinition[];
  validationRefDataKeys: string[];
  sampleRowCount: number;
  instructionGuide: InstructionGuideSection[];
}

export interface InstructionGuideSection {
  section: string;
  details: string;
  description: string;
  example: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// PLATFORM-SPECIFIC VALIDATION SWITCH
// ═══════════════════════════════════════════════════════════════════════════
// When true, platform-specific fields are conditionally required:
//   - ADF Factory Name: required when Platform = ADF
//   - Workspace Name: required when Platform = ADB
//   - Application: required when Scheduler = Control-M or Control-M-PGT
//   - Folder Path: required when Scheduler = Control-M or Control-M-PGT
// When false, these fields are always optional (no validation enforced).
// ═══════════════════════════════════════════════════════════════════════════
export const ENABLE_PLATFORM_VALIDATION = false;

// IBP Template Configuration
export const IBP_TEMPLATE: TemplateConfig = {
  id: 'ibp-gdo-bulk-upload',
  name: 'IBP GDO Bulk Upload Template',
  description: 'Template for bulk uploading Global Data Objects (GDOs) for IBP business stream',
  businessStream: 'IBP',
  fileName: 'GDO_Bulk_Upload_Template.xlsx',
  sheetName: 'GDO data',
  columns: [
    // Program-level attributes (GDO structure)
    {
      key: 'gdoName',
      label: 'GDO Name *',
      comment: 'Must start with GDO-<alphanumeric> (e.g., GDO-001, GDO-A1B). Max 64 chars. Alphanumeric + special: !@#$%^&*()_+=-|}{":?><][;\'\\/.,',
      scope: 'program',
      required: true,
      width: 56.7,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'pattern',
          pattern: /^[a-zA-Z0-9 !@#$%^&*()_+=\-|{}":?><\]\[;'\\\/.,]*$/,
          message: 'We accept only these special characters: a-zA-Z0-9 !@#$%^&*()_+=-|}{":?><][;\'\\/.,',
        },
        {
          type: 'pattern',
          pattern: /^GDO-[a-zA-Z0-9]+/,
          message: 'GDO name should start with GDO-<alphanumeric>',
        },
        {
          type: 'custom',
          message: 'GDO name is too long. Maximum allowed: 64 characters',
          customValidator: (value: string) => {
            return value.length > 64 ? `GDO name is too long (${value.length} chars). Maximum allowed: 64 characters` : null;
          },
        },
        {
          type: 'dependent',
          message: 'Duplicate GDO Name',
          dependsOn: ['sector', 'market', 'report', 'origin', 'destination', 'jobName', 'schedulerName'],
        },
      ],
      sampleValues: ['GDO-001', 'GDO-A1B', 'GDO-ABC'],
      description: 'Unique identifier for the Global Data Object',
      example: 'GDO-001, GDO-A1B, GDO-ABC',
    },
    {
      key: 'sector',
      label: 'Sector *',
      comment: (refData: any) => `Exactly one of: ${refData?.sectors?.join(', ') || '(none available)'} (case-insensitive)`,
      scope: 'program',
      required: true,
      width: 29.6,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'reference',
          referenceType: 'sectors',
          message: 'Invalid sector. Valid options loaded from database',
        },
      ],
      sampleValues: ['PBNA', 'PFNA', 'LATAM'],
      description: 'Business sector or division',
      example: 'PBNA, PFNA, LATAM',
    },
    {
      key: 'market',
      label: 'Market / Country *',
      comment: (refData: any) => `Valid market from the Markets collection (e.g., ${refData?.markets?.join(', ') || '(none available)'})`,
      scope: 'program',
      required: true,
      width: 16.4,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'reference',
          referenceType: 'markets',
          message: 'Invalid market. Valid options loaded from database',
        },
      ],
      sampleValues: ['BNL', 'FRANCE', 'BENELUX'],
      description: 'Geographic market or country',
      example: 'BNL, FRANCE, BENELUX',
    },
    {
      key: 'report',
      label: 'Report *',
      comment: (refData: any) => `Exactly one of: ${refData?.reports?.join(', ') || '(none available)'} (case-insensitive)`,
      scope: 'program',
      required: true,
      width: 7.9,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'reference',
          referenceType: 'reports',
          message: 'Invalid report. Valid options loaded from database',
        },
      ],
      sampleValues: ['iS&OE', 'Demand Plan', 'Supply Plan'],
      description: 'Associated report or business process',
      example: 'iS&OE, Demand Plan, Supply Plan',
    },
    {
      key: 'origin',
      label: 'Origin *',
      comment: 'Source system or data origin (required)',
      scope: 'program',
      required: true,
      width: 7.3,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
      ],
      sampleValues: ['o9', 'SAP', 'EDW'],
      description: 'Source system or data origin',
      example: 'o9, SAP, EDW',
    },
    {
      key: 'destination',
      label: 'Destination *',
      comment: 'Target system or data destination (required)',
      scope: 'program',
      required: true,
      width: 11.6,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
      ],
      sampleValues: ['Snowflake', 'ADW', 'o9'],
      description: 'Target system or data destination',
      example: 'Snowflake, ADW, o9',
    },
    {
      key: 'owner',
      label: 'Owner *',
      comment: 'Owner name (mandatory)',
      scope: 'program',
      required: true,
      width: 10.6,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
      ],
      sampleValues: ['John Doe', 'Jane Smith', 'Bob Wilson'],
      description: 'Primary owner of the GDO',
      example: 'John Doe, Jane Smith',
    },
    {
      key: 'gdoDescription',
      label: 'GDO Description/Label',
      comment: 'GDO Description/Label (optional)',
      scope: 'program',
      required: false,
      width: 20.8,
      validation: [],
      sampleValues: ['Supply data load for region', 'Demand plan data transfer', 'PFNA sales data refresh'],
      description: 'Optional description or label for the GDO',
      example: 'Supply data load for region',
    },
    {
      key: 'gdoCategory',
      label: 'GDO Category',
      comment: 'GDO Category (optional)',
      scope: 'program',
      required: false,
      width: 18.1,
      validation: [],
      sampleValues: ['Data Asset', 'Analytics', 'Reporting'],
      description: 'Optional category for the GDO',
      example: 'Data Asset',
    },
    {
      key: 'gdoDependentOn',
      label: 'GDO Dependent On',
      comment: 'GDO Dependent On - comma-separated GDO names this GDO depends on (optional). Use NONE to clear all dependencies.',
      scope: 'program',
      required: false,
      width: 18.1,
      validation: [
        {
          type: 'custom',
          message: 'Invalid dependency format',
          customValidator: (value: string, row: Record<string, any>, refData?: any) => {
            if (!value || value.toUpperCase() === 'NONE') return null;
            const deps = value.split(',').map(s => s.trim()).filter(Boolean);
            for (const dep of deps) {
              if (!dep.startsWith('GDO-')) {
                return `Dependency "${dep}" must start with GDO-`;
              }
              if (dep.toLowerCase() === row.gdoName?.toLowerCase()) {
                return 'GDO cannot depend on itself';
              }
            }
            return null;
          },
        },
      ],
      sampleValues: ['GDO-002', 'GDO-001,GDO-003', 'NONE'],
      description: 'Comma-separated GDO names this GDO depends on',
      example: 'GDO-002, GDO-003',
    },
    {
      key: 'stageInboundOutbound',
      label: 'Stage Inbound/Outbound to o9',
      comment: 'Stage Inbound/Outbound to o9: Inbound, Outbound, or Both (optional)',
      scope: 'program',
      required: false,
      width: 27.6,
      validation: [
        {
          type: 'enum',
          allowedValues: ['Inbound', 'Outbound', 'Both'],
          message: 'Invalid value. Valid options: Inbound, Outbound, Both',
        },
      ],
      sampleValues: ['Inbound', 'Outbound', 'Both'],
      description: 'Data flow direction relative to o9',
      example: 'Inbound, Outbound, Both',
    },
    {
      key: 'gdoStep',
      label: 'GDO step',
      comment: '1, 2, 3, 4 etc..',
      scope: 'program',
      required: false,
      width: 9.2,
      validation: [],
      sampleValues: ['1', '2', '3'],
      description: 'Sequential step number for the GDO',
      example: '1, 2, 3',
    },
    {
      key: 'dataOwner',
      label: 'Data Owner',
      comment: 'Data Owner (optional)',
      scope: 'program',
      required: false,
      width: 12.5,
      validation: [],
      sampleValues: ['Data Owner 1', 'Data Owner 2'],
      description: 'Person responsible for the data',
      example: 'Data Owner 1',
    },
    {
      key: 'dataSteward',
      label: 'Data Steward',
      comment: 'Data Steward (optional)',
      scope: 'program',
      required: false,
      width: 13.9,
      validation: [],
      sampleValues: ['Data Steward 1', 'Data Steward 2'],
      description: 'Person responsible for data quality',
      example: 'Data Steward 1',
    },
    // Job-level attributes
    {
      key: 'platform',
      label: 'Platform *',
      comment: (refData: any) => {
        const map = refData?.platformSchedulerMap;
        if (map && Object.keys(map).length > 0) {
          const platforms = Object.keys(map)
            .map(p => p.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
            .join(', ');
          return `Valid platforms: ${platforms} (case-insensitive)`;
        }
        return 'Platform name (required)';
      },
      scope: 'job',
      required: true,
      width: 9.9,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'custom',
          message: 'Invalid platform',
          customValidator: (value: string, row: Record<string, any>, refData?: any) => {
            const map = refData?.platformSchedulerMap;
            if (!map || Object.keys(map).length === 0) return null; // Skip if map not loaded
            const platformLower = value.toLowerCase();
            if (!map[platformLower]) {
              const validPlatforms = Object.keys(map)
                .map(p => p.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
                .join(', ');
              return `Invalid platform "${value}". Valid options: ${validPlatforms}`;
            }
            return null;
          },
        },
      ],
      sampleValues: ['ADF', 'BODS', 'Teradata'],
      description: 'Platform where the job runs',
      example: 'ADF, BODS, Teradata',
    },
    {
      key: 'schedulerName',
      label: 'Scheduler Name *',
      comment: (refData: any) => {
        const map = refData?.platformSchedulerMap;
        if (map && Object.keys(map).length > 0) {
          const combos = (Object.entries(map) as [string, string[]][])
            .map(([p, s]) => `${p.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}→${s.join('|')}`)
            .join(', ');
          return `Scheduler must match the platform. Valid combinations: ${combos}`;
        }
        return 'Scheduler name for the platform (required)';
      },
      scope: 'job',
      required: true,
      width: 17.0,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'custom',
          message: 'Invalid scheduler for this platform',
          customValidator: (value: string, row: Record<string, any>, refData?: any) => {
            const map = refData?.platformSchedulerMap;
            if (!map || Object.keys(map).length === 0) return null; // Skip if map not loaded
            const platformLower = (row.platform || '').toLowerCase();
            const validSchedulers = map[platformLower];
            if (!validSchedulers) return null; // Platform validation will catch invalid platform
            const schedulerLower = value.toLowerCase();
            const isValid = (validSchedulers as string[]).some((s: string) => s.toLowerCase() === schedulerLower);
            if (!isValid) {
              return `Invalid scheduler "${value}" for platform "${row.platform}". Valid options: ${validSchedulers.join(', ')}`;
            }
            return null;
          },
        },
      ],
      sampleValues: ['ADF', 'Control-M-PGT', 'HWA'],
      description: 'Job scheduler or orchestration tool (must match platform)',
      example: 'ADF, Control-M-PGT, HWA',
    },
    {
      key: 'jobName',
      label: 'Job Name *',
      comment: 'Job name for the scheduler (required)',
      scope: 'job',
      required: true,
      width: 14.2,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
      ],
      sampleValues: ['GDO_PBNA_Daily_Load', 'ADF_PFNA_Extract', 'BODS_EMEA_Transform'],
      description: 'Name of the job in the scheduler',
      example: 'GDO_Job_A, ADF_Pipeline_1',
    },
    // Platform-specific ES enrichment fields (grouped with Platform/Scheduler/JobName)
    {
      key: 'factoryName',
      label: 'ADF Factory Name',
      comment: 'REQUIRED when Platform = ADF. Enter the name of the Azure Data Factory (ADF) instance where this pipeline runs. Find this in Azure Portal > Data Factories.\nWithout this, the system cannot locate the pipeline in Elasticsearch.\n\nExamples:\npepwise-prod-eus2\npepwise-dev-wus2\nibp-analytics-eu',
      scope: 'job',
      required: false,
      width: 22.0,
      validation: [
        {
          type: 'custom',
          message: 'ADF Factory Name is required when Platform is ADF',
          customValidator: (value: string, row: Record<string, any>) => {
            if (!ENABLE_PLATFORM_VALIDATION) return null;
            const platform = (row.platform || '').toLowerCase().trim();
            if (platform === 'adf') {
              if (!value || !value.trim()) {
                return 'ADF Factory Name is required when Platform is ADF. Enter the ADF instance name (e.g., pepwise-prod-eus2). Without this, the system cannot locate the pipeline in Elasticsearch.';
              }
            }
            return null;
          },
        },
      ],
      sampleValues: ['pepwise-prod-eus2', 'pepwise-dev-wus2'],
      description: 'ADF instance/factory name (required for ADF platform)',
      example: 'pepwise-prod-eus2',
    },
    {
      key: 'workspaceName',
      label: 'Workspace Name',
      comment: 'REQUIRED when Platform = ADB. Enter the Databricks workspace name where this job runs. Find this in Databricks > Workspace URL (e.g., https://adb-xxxxxxx.xxx.azuredatabricks.net).\nWithout this, the system cannot locate the job in Elasticsearch.\n\nExamples:\npepwise-prod-workspace\npepwise-dev-workspace\nibp-analytics-prod',
      scope: 'job',
      required: false,
      width: 24.0,
      validation: [
        {
          type: 'custom',
          message: 'Workspace Name is required when Platform is ADB',
          customValidator: (value: string, row: Record<string, any>) => {
            if (!ENABLE_PLATFORM_VALIDATION) return null;
            const platform = (row.platform || '').toLowerCase().trim();
            if (platform === 'adb') {
              if (!value || !value.trim()) {
                return 'Workspace Name is required when Platform is ADB. Enter the Databricks workspace name (e.g., pepwise-prod-workspace). Without this, the system cannot locate the job in Elasticsearch.';
              }
            }
            return null;
          },
        },
      ],
      sampleValues: ['pepwise-prod-workspace', 'pepwise-dev-workspace'],
      description: 'Databricks workspace name (required for ADB platform)',
      example: 'pepwise-prod-workspace',
    },
    {
      key: 'triggerName',
      label: 'Trigger Name',
      comment: 'Optional for ADF jobs. Enter the ADF trigger name that runs this pipeline. Each ADF pipeline can have multiple triggers (Manual, All, or named triggers).\nLeave empty for manual execution.\nLeave empty if not an ADF pipeline.\n\nExamples:\ntrg_daily_load\ntrg_hourly_sync\nManual\nAll',
      scope: 'job',
      required: false,
      width: 22.0,
      validation: [],
      sampleValues: ['trg_daily_load', 'trg_hourly_sync', 'Manual', 'All'],
      description: 'ADF trigger name (optional, for ADF platform)',
      example: 'trg_daily_load',
    },
    {
      key: 'application',
      label: 'Application',
      comment: 'REQUIRED when Scheduler = Control-M or Control-M-PGT (for BODS or Teradata platforms). Enter the Control-M Application name (APPLIC) that owns this job.\nThis is used to build the unique Job ID in the format: APPLIC_SCHEDTAB_JobName.\nWithout this, the system cannot match the job in Elasticsearch.\n\nExamples:\nIBP_PROD\nDP_DEV\nSCHED_TAB\nSALES_DATA',
      scope: 'job',
      required: false,
      width: 16.0,
      validation: [
        {
          type: 'custom',
          message: 'Application is required for Control-M schedulers',
          customValidator: (value: string, row: Record<string, any>) => {
            if (!ENABLE_PLATFORM_VALIDATION) return null;
            const scheduler = (row.schedulerName || '').toLowerCase().trim();
            const isControlM = scheduler === 'control-m' || scheduler === 'control-m-pgt';
            if (isControlM) {
              if (!value || !value.trim()) {
                return 'Application (APPLIC) is required when Scheduler is Control-M or Control-M-PGT. Enter the Control-M application name (e.g., IBP_PROD). Without this, the system cannot build the unique Job ID.';
              }
            }
            return null;
          },
        },
      ],
      sampleValues: ['IBP_PROD', 'DP_DEV', 'SCHED TAB'],
      description: 'Control-M application name (required for Control-M schedulers)',
      example: 'IBP_PROD',
    },
    {
      key: 'folderPath',
      label: 'Folder Path',
      comment: 'REQUIRED when Scheduler = Control-M or Control-M-PGT (for BODS or Teradata platforms). Enter the Control-M Schedule Tab / Folder path (SCHEDTAB) where this job is located.\nUse forward slash (/) to separate folder levels. This is used to build the unique Job ID in the format: APPLIC_SCHEDTAB_JobName.\nWithout this, the system cannot match the job in Elasticsearch.\n\nExamples:\nFOLDER/SUBFOLDER\nROOT_FOLDER/DEEP/NESTED\nIBP_JOBS/DAILY_LOADS',
      scope: 'job',
      required: false,
      width: 22.0,
      validation: [
        {
          type: 'custom',
          message: 'Folder Path is required for Control-M schedulers',
          customValidator: (value: string, row: Record<string, any>) => {
            if (!ENABLE_PLATFORM_VALIDATION) return null;
            const scheduler = (row.schedulerName || '').toLowerCase().trim();
            const isControlM = scheduler === 'control-m' || scheduler === 'control-m-pgt';
            if (isControlM) {
              if (!value || !value.trim()) {
                return 'Folder Path (SCHEDTAB) is required when Scheduler is Control-M or Control-M-PGT. Enter the Control-M folder path (e.g., FOLDER/SUBFOLDER). Without this, the system cannot build the unique Job ID.';
              }
            }
            return null;
          },
        },
      ],
      sampleValues: ['FOLDER/SUBFOLDER', 'ROOT_FOLDER/DEEP'],
      description: 'Control-M schedule tab/folder path (required for Control-M schedulers)',
      example: 'FOLDER/SUBFOLDER',
    },
    {
      key: 'scheduleDays',
      label: 'Schedule Days *',
      comment: 'Comma-separated full day names (case-insensitive): Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday. Use All for every day.',
      scope: 'job',
      required: true,
      width: 18.0,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'custom',
          message: 'Invalid day names. Use full names: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday, or All.',
          customValidator: (value: string) => {
            if (!value) return null;
            const validDays = ['all', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
            const days = value.split(',').map(d => d.trim().toLowerCase());
            for (const day of days) {
              if (!validDays.includes(day)) {
                return `Invalid day "${day}". Use full names: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday, or All.`;
              }
            }
            return null;
          },
        },
      ],
      sampleValues: ['Monday,Wednesday,Friday', 'All', 'Monday,Tuesday,Wednesday,Thursday,Friday'],
      description: 'Days the job runs on (full day names, case-insensitive)',
      example: 'Monday,Wednesday,Friday, All, Tuesday,Thursday',
    },
    {
      key: 'executionStartTime',
      label: 'Execution Start Time',
      comment: '24-hour time format HH:MM (e.g., 06:00, 14:30). Required with either End Time or Avg Execution Time. At least 2 of Start Time / End Time / Avg Time must be provided.',
      scope: 'job',
      required: false,
      width: 16.0,
      validation: [
        {
          type: 'pattern',
          pattern: /^([01]\d|2[0-3]):[0-5]\d$/,
          message: 'Invalid time format. Expected HH:MM in 24-hour format (e.g., 06:00, 14:30)',
        },
        {
          type: 'custom',
          message: 'At least 2 of Start Time, End Time, Avg Execution Time are required',
          customValidator: (_value: string, row: any) => {
            const hasStart = !!(row.executionStartTime || '').trim();
            const hasEnd = !!(row.executionEndTime || '').trim();
            const hasAvg = !!(row.averageExecutionTime || '').trim();
            const filled = [hasStart, hasEnd, hasAvg].filter(Boolean).length;
            if (filled < 2) {
              return 'At least 2 of Start Time, End Time, Avg Execution Time are required (e.g., Start Time + End Time, or Start Time + Avg Time, or End Time + Avg Time)';
            }
            return null;
          },
        },
      ],
      sampleValues: ['06:00', '09:00', '14:30'],
      description: 'Job execution start time in 24-hour HH:MM format',
      example: '06:00, 09:00, 14:30, 23:59',
    },
    {
      key: 'executionEndTime',
      label: 'Execution End Time',
      comment: '24-hour time format HH:MM (e.g., 08:00, 16:30). Required with either Start Time or Avg Execution Time. At least 2 of Start Time / End Time / Avg Time must be provided.',
      scope: 'job',
      required: false,
      width: 16.0,
      validation: [
        {
          type: 'pattern',
          pattern: /^([01]\d|2[0-3]):[0-5]\d$/,
          message: 'Invalid time format. Expected HH:MM in 24-hour format (e.g., 08:00, 16:30)',
        },
      ],
      sampleValues: ['08:00', '16:30', ''],
      description: 'Job execution end time in 24-hour HH:MM format',
      example: '08:00, 16:30',
    },
    {
      key: 'timezone',
      label: 'Timezone *',
      comment: 'Timezone for schedule info and SLA timings (e.g., CST, EST, PST, UTC, America/New_York, Europe/London, Asia/Tokyo)',
      scope: 'job',
      required: true,
      width: 10.2,
      validation: [
        {
          type: 'required',
          message: 'Field is required',
        },
        {
          type: 'custom',
          message: 'Invalid timezone',
          customValidator: (value: string) => {
            // Accept all standard timezone formats:
            // 1. Abbreviations: 2-5 uppercase letters (UTC, GMT, EST, EDT, CST, CDT, CET, CEST, IST, AEST, JST, etc.)
            // 2. UTC offsets: UTC±N or UTC±N:MM (e.g., UTC-5, UTC+5:30, UTC+14)
            // 3. IANA names: Area/Location (e.g., America/New_York, Europe/London, Asia/Tokyo)
            const TIMEZONE_REGEX = /^(UTC[+-]?\d{1,2}(:\d{2})?|[A-Z]{2,5}|[A-Za-z]+\/[A-Za-z_]+)$/;
            return TIMEZONE_REGEX.test(value) ? null : `Invalid timezone "${value}". Examples: CST, EST, CET, IST, UTC, UTC-5, America/New_York, Europe/London`;
          },
        },
      ],
      sampleValues: ['CST', 'EST', 'PST'],
      description: 'Timezone for schedule and SLA timings',
      example: 'CST, EST, PST, UTC, America/New_York',
    },
    {
      key: 'averageExecutionTime',
      label: 'Average Execution Time',
      comment: 'Specify the typical time this job takes to complete (e.g., 20 min, 30 min). Required with either Start Time or End Time. At least 2 of Start Time / End Time / Avg Time must be provided.',
      scope: 'job',
      required: false,
      width: 20.7,
      validation: [],
      sampleValues: ['20 min', '30 min', '1 hour'],
      description: 'Typical job execution time',
      example: '20 min, 30 min',
    },
    {
      key: 'loadFrequency',
      label: 'Load Frequency',
      comment: 'Enter how frequently this job runs (e.g., Hourly, Daily, Weekly, Twice-a-Week, Monthly). Optional.',
      scope: 'job',
      required: false,
      width: 13.5,
      validation: [],
      sampleValues: ['Hourly', 'Daily', 'Weekly'],
      description: 'Job execution frequency',
      example: 'Hourly, Daily, Weekly',
    },
    {
      key: 'slaType',
      label: 'SLA Type',
      comment: 'SLA type: Daily, Weekly, Monthly, Bi-Weekly, Quarterly (optional)',
      scope: 'job',
      required: false,
      width: 10.2,
      validation: [
        {
          type: 'enum',
          allowedValues: ['Daily', 'Weekly', 'Monthly', 'Bi-Weekly', 'Quarterly'],
          message: 'Invalid SLA type. Valid options: Daily, Weekly, Monthly, Bi-Weekly, Quarterly',
        },
      ],
      sampleValues: ['Daily', 'Weekly', 'Monthly'],
      description: 'Service Level Agreement frequency',
      example: 'Daily, Weekly, Monthly',
    },
    {
      key: 'slaDeadlineType',
      label: 'SLA Deadline Type',
      comment: 'Clock (specific start/end time window) or Interval (duration-based). If empty, derived from SLA Type: Daily→Clock, Weekly→Interval. (optional)',
      scope: 'job',
      required: false,
      width: 15.0,
      validation: [
        {
          type: 'enum',
          allowedValues: ['Clock', 'Interval'],
          message: 'Invalid SLA deadline type. Valid options: Clock, Interval',
        },
      ],
      sampleValues: ['Clock', 'Interval'],
      description: 'SLA deadline type: Clock (time window) or Interval (duration)',
      example: 'Clock = must complete by 18:00, Interval = 120 min window',
    },
    {
      key: 'slaDays',
      label: 'SLA Days',
      comment: 'All Days or comma-separated: Mon,Tue,Wed,Thu,Fri,Sat,Sun (no duplicates)',
      scope: 'job',
      required: false,
      width: 19.3,
      validation: [
        {
          type: 'pattern',
          pattern: /^(All Days|(Mon|Tue|Wed|Thu|Fri|Sat|Sun)(,(Mon|Tue|Wed|Thu|Fri|Sat|Sun))*)$/,
          message: 'Invalid value. Use "All Days" or comma-separated days: Mon,Tue,Wed,Thu,Fri,Sat,Sun',
        },
      ],
      sampleValues: ['All Days', 'Mon,Tue,Wed,Thu,Fri', 'Mon,Wed,Fri'],
      description: 'Days for SLA monitoring',
      example: 'All Days, Mon,Tue,Wed,Thu,Fri',
    },
    {
      key: 'slaStartTime',
      label: 'SLA Start Time',
      comment:
        'HH:MM 24-hr format (e.g., 09:10). Comma-separate multiple start times to create multiple SLA slots in one day for cyclic jobs (e.g., 06:00,14:00,22:00). Slots pair by position with SLA End Time / SLA Max Runtime (min) — use the same count. (optional)',
      scope: 'job',
      required: false,
      width: 14.8,
      validation: [
        {
          type: 'pattern',
          pattern: /^([01]\d|2[0-3]):[0-5]\d(\s*,\s*([01]\d|2[0-3]):[0-5]\d)*$/,
          message:
            'Invalid time. Expected 24-hr format HH:MM, comma-separated for multiple SLA slots (e.g., 08:00 or 06:00,14:00)',
        },
      ],
      sampleValues: ['08:00', '06:00,14:00', '00:00,06:00,12:00,18:00'],
      description:
        'SLA start time(s) in 24-hour format — one value, or comma-separated values to create multiple SLA slots in a day',
      example: '08:00 or 06:00,14:00 (two SLA slots)',
    },
    {
      key: 'slaEndTime',
      label: 'SLA End Time',
      comment:
        'HH:MM 24-hr format, must be after Start Time (set SLA End Day Offset for overnight deadlines). Comma-separate multiple end times for multiple SLA slots — paired by position with SLA Start Time (e.g., 06:00,14:00 ↔ 10:00,18:00). (optional)',
      scope: 'job',
      required: false,
      width: 14.1,
      validation: [
        {
          type: 'pattern',
          pattern: /^([01]\d|2[0-3]):[0-5]\d(\s*,\s*([01]\d|2[0-3]):[0-5]\d)*$/,
          message:
            'Invalid time. Expected 24-hr format HH:MM, comma-separated for multiple SLA slots (e.g., 17:00 or 10:00,18:00)',
        },
      ],
      sampleValues: ['18:00', '10:00,18:00', '04:00,10:00,16:00,02:00'],
      description:
        'SLA end time(s) in 24-hour format — each value pairs with a SLA Start Time to form one SLA slot',
      example: '18:00 or 10:00,18:00 (two SLA slots)',
    },
    {
      key: 'slaCriteria',
      label: 'SLA Criteria',
      comment:
        'SLA Criteria (optional): ANY RUN — SLA is met if at least one run completes inside the window; ALL RUNS — every run inside the window must complete; SELECTED RUNS — only the selected SLA slots are evaluated; LATEST RUN — evaluated on the latest run of the day (deadline may roll into the next day)',
      scope: 'job',
      required: false,
      width: 22.4,
      validation: [
        {
          type: 'enum',
          allowedValues: [
            'ANY RUN',
            'Any Run',
            'ALL RUNS',
            'All Runs',
            'SELECTED RUNS',
            'Selected Run',
            'LATEST RUN',
            'Latest Run',
          ],
          message:
            'Invalid SLA Criteria. Valid options: ANY RUN, ALL RUNS, SELECTED RUNS, LATEST RUN',
        },
      ],
      sampleValues: ['ANY RUN', 'ALL RUNS', 'SELECTED RUNS', 'LATEST RUN'],
      description:
        "Rule the SLA processor uses to evaluate runs inside the SLA window: ANY RUN (at least one run), ALL RUNS (every run), SELECTED RUNS (selected slots only), LATEST RUN (the day's latest run)",
      example:
        'ANY RUN = at least one run must complete; LATEST RUN = latest run of the day is evaluated (may cross midnight)',
    },
    {
      key: 'slaEndDayOffset',
      label: 'SLA End Day Offset',
      comment: 'Number of days after start when SLA ends. Use 0 for same-day deadlines, 1 for overnight (e.g., 22:00 → 06:00 next day). Required when SLA End Time < SLA Start Time. (optional)',
      scope: 'job',
      required: false,
      width: 18.0,
      validation: [
        {
          type: 'pattern',
          pattern: /^[0-2]$/,
          message: 'Invalid value. Expected: 0 (same day), 1 (next day), or 2 (2 days later)',
        },
      ],
      sampleValues: ['0', '1', '2'],
      description: 'Days offset for overnight SLA deadlines',
      example: '0 = same day, 1 = next day (for 22:00 → 06:00)',
    },
    {
      key: 'slaCriticality',
      label: 'SLA Criticality',
      comment: 'Is this SLA time window critical? Y = yes (default), N = no. (optional)',
      scope: 'job',
      required: false,
      width: 12.0,
      validation: [
        {
          type: 'enum',
          allowedValues: ['Y', 'N'],
          message: 'Invalid value. Valid options: Y (critical), N (non-critical)',
        },
      ],
      sampleValues: ['Y', 'N'],
      description: 'Slot criticality flag',
      example: 'Y = critical (default), N = non-critical',
    },
    {
      key: 'slaMaxRuntimeMinutes',
      label: 'SLA Max Runtime (min)',
      comment:
        'Explicit maximum runtime in minutes for INTERVAL SLA — takes precedence over calculating from SLA Start/End Times. Comma-separate one value per SLA slot for cyclic jobs (e.g., 30,45,60). (optional)',
      scope: 'job',
      required: false,
      width: 20.0,
      validation: [
        {
          type: 'pattern',
          pattern: /^[1-9]\d*(\s*,\s*[1-9]\d*)*$/,
          message:
            'Invalid value. Must be positive integers, comma-separated for multiple SLA slots (e.g., 60 or 30,45,60)',
        },
      ],
      sampleValues: ['60', '120', '30,45,60'],
      description:
        'Maximum runtime in minutes for INTERVAL SLA — one value, or one value per SLA slot',
      example: '60 = 1 hour; 30,45,60 = three SLA slots of 30/45/60 minutes',
    },
    {
      key: 'slaScheduleTrigger',
      label: 'SLA Schedule Trigger',
      comment: 'How the SLA is triggered: Scheduled (runs on fixed schedule) or Triggered (event-driven). Default: Scheduled. (optional)',
      scope: 'job',
      required: false,
      width: 18.0,
      validation: [
        {
          type: 'enum',
          allowedValues: ['Scheduled', 'Triggered'],
          message: 'Invalid value. Valid options: Scheduled, Triggered',
        },
      ],
      sampleValues: ['Scheduled', 'Triggered'],
      description: 'SLA trigger type',
      example: 'Scheduled = fixed schedule, Triggered = event-driven',
    },
    {
      key: 'remarks',
      label: 'Remarks / Error Flag',
      comment: 'System populated — do not edit',
      scope: 'system',
      required: false,
      width: 18.4,
      validation: [],
      description: 'System-generated error messages',
      example: 'Auto-populated by validation',
    },
    // Extended job-level attributes (Z-CC columns)
    {
      key: 'forumsOrSystems',
      label: 'Forums Or Systems',
      comment: 'Identify the forum or system associated with this job or data asset, such as Supply Control, Demand Control, or PFE Input.\n\nExample:\nSupply Control',
      scope: 'job',
      required: false,
      width: 16.6,
      validation: [],
      sampleValues: ['Supply Control', 'Demand Control', 'PFE Input'],
      description: 'Forum or system associated with the job',
      example: 'Supply Control, Demand Control',
    },
    {
      key: 'inScope',
      label: 'In Scope',
      comment: 'Indicate whether this job is currently in scope for active runs and operations.\n\nExample:\nYes',
      scope: 'job',
      required: false,
      width: 8.1,
      validation: [
        {
          type: 'enum',
          allowedValues: ['Yes', 'No'],
          message: 'Invalid value. Valid options: Yes, No',
        },
      ],
      sampleValues: ['Yes', 'No'],
      description: 'Whether the job is in scope for active operations',
      example: 'Yes, No',
    },
    {
      key: 'jobType',
      label: 'Job Type',
      comment: 'Specify the type of job being executed, such as BODS or Data Asset.\n\nExample:\nBODS',
      scope: 'job',
      required: false,
      width: 7.8,
      validation: [],
      sampleValues: ['BODS', 'Data Asset', 'ADF'],
      description: 'Type of job being executed',
      example: 'BODS, Data Asset',
    },
    {
      key: 'release',
      label: 'Release',
      comment: 'Enter the release cycle associated with this job or data asset.\n\nExample:\nR1A',
      scope: 'job',
      required: false,
      width: 7.7,
      validation: [],
      sampleValues: ['R1A', 'R1B', 'R2A'],
      description: 'Release cycle for the job',
      example: 'R1A, R1B',
    },
    {
      key: 'area',
      label: 'Area',
      comment: 'Specify the planning area this job supports, such as Demand Planning (DP) or Supply Planning (SP).\n\nExample:\nSP',
      scope: 'job',
      required: false,
      width: 4.8,
      validation: [],
      sampleValues: ['DP', 'SP', 'FP'],
      description: 'Planning area supported by the job',
      example: 'DP, SP',
    },
    {
      key: 'pushPullMechanism',
      label: 'Push Pull Mechanism',
      comment: 'Indicate whether data is pushed to or pulled from the source system.\n\nExample:\nPull',
      scope: 'job',
      required: false,
      width: 19.2,
      validation: [],
      sampleValues: ['Push', 'Pull', 'Both'],
      description: 'Data transfer mechanism',
      example: 'Push, Pull',
    },
    {
      key: 'timeLapseBetweenSourceUpdateAndLoadInLanding',
      label: 'Time Lapse Between Source Update And Load In Landing',
      comment: 'Enter the elapsed time between the source system update and the data load into the landing zone.\n\nExample:\n10 min',
      scope: 'job',
      required: false,
      width: 47.5,
      validation: [],
      sampleValues: ['10 min', '15 min', '30 min'],
      description: 'Time between source update and data load',
      example: '10 min, 15 min',
    },
    {
      key: 'dependenciesWithOtherJobsReferSequence',
      label: 'Dependencies With Other Jobs Refer Sequence',
      comment: 'List the sequence numbers of other jobs this job depends on, separated by commas.\n\nExample:\n17.2.1, 17.2.2, 17.2.3',
      scope: 'job',
      required: false,
      width: 39.6,
      validation: [],
      sampleValues: ['17.2.1, 17.2.2', '18.1.1'],
      description: 'Job dependencies by sequence number',
      example: '17.2.1, 17.2.2, 17.2.3',
    },
    {
      key: 'gdoDependencies',
      label: 'Gdo Dependencies',
      comment: 'Identify any other GDOs that this GDO depends on.\n\nExample:\nGDO3_Customer, GDO1_Product',
      scope: 'job',
      required: false,
      width: 16.8,
      validation: [],
      sampleValues: ['GDO3_Customer', 'GDO1_Product'],
      description: 'Other GDO dependencies',
      example: 'GDO3_Customer, GDO1_Product',
    },
    {
      key: 'jobOwner',
      label: 'Job Owner',
      comment: 'Enter the name of the assignment group or individual who owns this job.\n\nExample:\nSamuel Stanley',
      scope: 'job',
      required: false,
      width: 9.2,
      validation: [],
      sampleValues: ['Samuel Stanley', 'Team Alpha'],
      description: 'Assignment group or individual owning the job',
      example: 'Samuel Stanley',
    },
    {
      key: 'dataSourceOfExtraction',
      label: 'Data Source Of Extraction',
      comment: 'Specify the system or layer from which the data is extracted.\n\nExample:\nEDF Landing',
      scope: 'job',
      required: false,
      width: 22.1,
      validation: [],
      sampleValues: ['EDF Landing', 'Source System', 'Data Lake'],
      description: 'System or layer for data extraction',
      example: 'EDF Landing',
    },
    {
      key: 'sourceFilesTablesViews',
      label: 'Source - Files/tables/views',
      comment: 'List the source tables, files, or views that feed this job, using the fully qualified schema and table name.\n\nExample:\nfdn_supplychain_internal.XTNProductAggregateGroupNetworkOptimizedReceipt',
      scope: 'job',
      required: false,
      width: 23.2,
      validation: [],
      sampleValues: ['fdn_supplychain_internal.XTNProductAggregateGroupNetworkOptimizedReceipt'],
      description: 'Source tables, files, or views',
      example: 'fdn_supplychain_internal.XTNProductAggregateGroupNetworkOptimizedReceipt',
    },
    {
      key: 'datasetType',
      label: 'Dataset Type',
      comment: 'Indicate the type of data asset this job handles, such as Master Data or Transactional Data.\n\nExample:\nMaster Data',
      scope: 'job',
      required: false,
      width: 11.2,
      validation: [],
      sampleValues: ['Master Data', 'Transactional Data'],
      description: 'Type of data asset',
      example: 'Master Data, Transactional Data',
    },
    {
      key: 'dataLoadingStrategy',
      label: 'Data Loading Strategy',
      comment: 'Specify whether the job performs a full or incremental data load.\n\nExample:\nIncremental',
      scope: 'job',
      required: false,
      width: 18.8,
      validation: [],
      sampleValues: ['Full', 'Incremental'],
      description: 'Data loading strategy',
      example: 'Full, Incremental',
    },
    {
      key: 'originalSourceDataAvailabilityCst',
      label: 'Original Source Data Availability Cst',
      comment: 'Indicate when the original source data becomes available, in Central Time (CST). Enter a specific time, or a descriptive value such as Near Real Time or Incremental if a fixed time does not apply.\n\nExample:\n09:30:00',
      scope: 'job',
      required: false,
      width: 30.3,
      validation: [],
      sampleValues: ['09:30:00', 'Near Real Time', 'Incremental'],
      description: 'Source data availability time in CST',
      example: '09:30:00, Near Real Time',
    },
    {
      key: 'eventOrScheduled',
      label: 'Event Or Scheduled',
      comment: 'Indicate whether this job is triggered by an event or runs on a fixed schedule.\n\nExample:\nScheduled',
      scope: 'job',
      required: false,
      width: 17.1,
      validation: [],
      sampleValues: ['Event', 'Scheduled'],
      description: 'Job trigger type',
      example: 'Event, Scheduled',
    },
    {
      key: 'errorHandlingAction',
      label: 'Error Handling Action',
      comment: 'Describe the action taken when this job encounters an error.\n\nExample:\nCreate an incident in SNOW',
      scope: 'job',
      required: false,
      width: 18.5,
      validation: [],
      sampleValues: ['Create an incident in SNOW', 'Retry automatically', 'Alert platform team'],
      description: 'Error handling procedure',
      example: 'Create an incident in SNOW',
    },
    {
      key: 'jobEndTimeCst',
      label: 'Job End Time Cst',
      comment: 'Enter the time this job completes, in Central Time (CST), using 24-hour format (HH:MM:SS).\n\nExample:\n09:30:00',
      scope: 'job',
      required: false,
      width: 14.5,
      validation: [],
      sampleValues: ['09:30:00', '17:00:00'],
      description: 'Job completion time in CST',
      example: '09:30:00',
    },
    {
      key: 'jobEndTimeUtc',
      label: 'Job End Time Utc',
      comment: 'Enter the time this job completes, in Coordinated Universal Time (UTC), using 24-hour format (HH:MM:SS).\n\nExample:\n14:30:00',
      scope: 'job',
      required: false,
      width: 14.6,
      validation: [],
      sampleValues: ['14:30:00', '22:00:00'],
      description: 'Job completion time in UTC',
      example: '14:30:00',
    },
    {
      key: 'typeOfTransferJob',
      label: 'Type Of Transfer Job',
      comment: 'Specify how data is transferred for this job, such as Manual, EDF Trigger, or DB Pull ADF.\n\nExample:\nEDF Trigger',
      scope: 'job',
      required: false,
      width: 16.9,
      validation: [],
      sampleValues: ['Manual', 'EDF Trigger', 'DB Pull ADF'],
      description: 'Data transfer method',
      example: 'Manual, EDF Trigger',
    },
    {
      key: 'hardGdoDependencies',
      label: 'Hard Gdo Dependencies',
      comment: 'List the GDOs that this job has a hard (mandatory) dependency on, noting soft dependencies separately if applicable.\n\nExample:\n3 (hard), 5 (soft)',
      scope: 'job',
      required: false,
      width: 21.2,
      validation: [],
      sampleValues: ['3 (hard), 5 (soft)', '1 (hard)'],
      description: 'Hard and soft GDO dependencies',
      example: '3 (hard), 5 (soft)',
    },
    {
      key: 'expectedExecutionWindows',
      label: 'Expected Execution Windows',
      comment: 'Enter the expected time window during which this job should run.\n\nExample:\n5:00 PM to 10:00 PM',
      scope: 'job',
      required: false,
      width: 25.9,
      validation: [],
      sampleValues: ['5:00 PM to 10:00 PM', '6:00 AM to 8:00 AM'],
      description: 'Expected execution time window',
      example: '5:00 PM to 10:00 PM',
    },
    {
      key: 'o9ErrorHandling',
      label: 'O9 Error Handling',
      comment: 'Describe how errors from this job are handled within the o9 system.\n\nExample:\nRetry automatically, then alert the platform team',
      scope: 'job',
      required: false,
      width: 15.4,
      validation: [],
      sampleValues: ['Retry automatically', 'Alert platform team'],
      description: 'Error handling in o9 system',
      example: 'Retry automatically, then alert the platform team',
    },
    {
      key: 'pipelineName',
      label: 'Pipeline Name',
      comment: 'Enter the name of the pipeline associated with this job, following the standard pl_ naming convention.\n\nExample:\npl_supply_chain_load',
      scope: 'job',
      required: false,
      width: 12.8,
      validation: [],
      sampleValues: ['pl_supply_chain_load', 'pl_demand_planning'],
      description: 'Pipeline name following pl_ convention',
      example: 'pl_supply_chain_load',
    },
    {
      key: 'executionDate',
      label: 'Execution Date',
      comment: 'Enter the date this job is executed.\n\nExample:\nFriday, July 24, 2026',
      scope: 'job',
      required: false,
      width: 13.4,
      validation: [],
      sampleValues: ['Friday, July 24, 2026', 'Monday, July 20, 2026'],
      description: 'Job execution date',
      example: 'Friday, July 24, 2026',
    },
    {
      key: 'workingDayToBeExecuted',
      label: 'Working Day To Be Executed',
      comment: 'Specify the working day on which this job is scheduled to execute, relative to the cycle start.\n\nExample:\nWD3',
      scope: 'job',
      required: false,
      width: 24.6,
      validation: [],
      sampleValues: ['WD1', 'WD2', 'WD3'],
      description: 'Working day relative to cycle start',
      example: 'WD1, WD2, WD3',
    },
    {
      key: 'jobGroup',
      label: 'Job Group',
      comment: 'Enter the group or category this job belongs to.\n\nExample:\nDMR Local',
      scope: 'job',
      required: false,
      width: 9.0,
      validation: [],
      sampleValues: ['DMR Local', 'Global', 'Regional'],
      description: 'Job group or category',
      example: 'DMR Local, Global',
    },
    {
      key: 'batchScope',
      label: 'Batch Scope',
      comment: 'Indicate whether this batch job runs at a global or local scope.\n\nExample:\nGlobal',
      scope: 'job',
      required: false,
      width: 11.3,
      validation: [],
      sampleValues: ['Global', 'Local'],
      description: 'Batch execution scope',
      example: 'Global, Local',
    },
    {
      key: 'jobStartTimeCetCest',
      label: 'Job Start Time Cet Cest',
      comment: 'Enter the time this job starts, in Central European Time (CET/CEST), using 24-hour format (HH:MM:SS).\n\nExample:\n08:00:00',
      scope: 'job',
      required: false,
      width: 19.2,
      validation: [],
      sampleValues: ['08:00:00', '09:15:00'],
      description: 'Job start time in CET/CEST',
      example: '08:00:00',
    },
    {
      key: 'jobEndTimeCetCest',
      label: 'Job End Time Cet Cest',
      comment: 'Enter the time this job completes, in Central European Time (CET/CEST), using 24-hour format (HH:MM:SS).\n\nExample:\n09:15:00',
      scope: 'job',
      required: false,
      width: 18.7,
      validation: [],
      sampleValues: ['09:15:00', '10:30:00'],
      description: 'Job completion time in CET/CEST',
      example: '09:15:00',
    },
    {
      key: 'op',
      label: 'Op',
      comment: 'Describe the operational timing rule for this job relative to the DMR cycle.\n\nExample:\n3 full days before DMR',
      scope: 'job',
      required: false,
      width: 3.5,
      validation: [],
      sampleValues: ['3 full days before DMR', '2 days before'],
      description: 'Operational timing rule',
      example: '3 full days before DMR',
    },
    {
      key: 'dependancyWithEvent',
      label: 'Dependancy With Event',
      comment: 'Describe the event this job depends on before it can start.\n\nExample:\nAfter FE MT Execution ends',
      scope: 'job',
      required: false,
      width: 20.8,
      validation: [],
      sampleValues: ['After FE MT Execution ends', 'After data load completes'],
      description: 'Event dependency',
      example: 'After FE MT Execution ends',
    },
    {
      key: 'dependencyOnForun',
      label: 'Dependency On Forun',
      comment: 'Specify the forum-related milestone this job depends on.\n\nExample:\nPrevious cycle PxF submission (RF & CoC Update)',
      scope: 'job',
      required: false,
      width: 19.1,
      validation: [],
      sampleValues: ['Previous cycle PxF submission (RF & CoC Update)'],
      description: 'Forum-related milestone dependency',
      example: 'Previous cycle PxF submission (RF & CoC Update)',
    },
    {
      key: 'dateOfExecution',
      label: 'Date Of Execution',
      comment: 'Enter the date this job is executed. This field mirrors the Execution Date column.\n\nExample:\nFriday, July 24, 2026',
      scope: 'job',
      required: false,
      width: 15.6,
      validation: [],
      sampleValues: ['Friday, July 24, 2026'],
      description: 'Job execution date (mirrors Execution Date)',
      example: 'Friday, July 24, 2026',
    },
    {
      key: 'month',
      label: 'Month',
      comment: 'Enter the numeric month in which this job runs.\n\nExample:\n7',
      scope: 'job',
      required: false,
      width: 6.5,
      validation: [],
      sampleValues: ['1', '7', '12'],
      description: 'Numeric month of execution',
      example: '7',
    },
    {
      key: 'firstDayOfMonth',
      label: '1st Day Of Month',
      comment: "Enter the first calendar day of the month for this job's cycle.\n\nExample:\n7/1/2026",
      scope: 'job',
      required: false,
      width: 15.2,
      validation: [],
      sampleValues: ['7/1/2026', '1/1/2026'],
      description: 'First calendar day of the month',
      example: '7/1/2026',
    },
    {
      key: 'dmrDate',
      label: 'Dmr Date',
      comment: 'Enter the date of the DMR (Demand Management Review) cycle.\n\nExample:\nThursday, July 23, 2026',
      scope: 'job',
      required: false,
      width: 8.5,
      validation: [],
      sampleValues: ['Thursday, July 23, 2026'],
      description: 'DMR cycle date',
      example: 'Thursday, July 23, 2026',
    },
    {
      key: 'smrDate',
      label: 'Smr Date',
      comment: 'Enter the date of the SMR (Supply Management Review) cycle.\n\nExample:\nThursday, July 30, 2026',
      scope: 'job',
      required: false,
      width: 8.4,
      validation: [],
      sampleValues: ['Thursday, July 30, 2026'],
      description: 'SMR cycle date',
      example: 'Thursday, July 30, 2026',
    },
    {
      key: 'workingDay',
      label: 'Working Day',
      comment: "Enter the working day date associated with this job's cycle.\n\nExample:\n7/1/2026",
      scope: 'job',
      required: false,
      width: 11.2,
      validation: [],
      sampleValues: ['7/1/2026'],
      description: 'Working day date',
      example: '7/1/2026',
    },
    {
      key: 'calenderDay',
      label: 'Calender Day',
      comment: "Enter the calendar day associated with this job's cycle.\n\nExample:\n7/2/2026",
      scope: 'job',
      required: false,
      width: 11.8,
      validation: [],
      sampleValues: ['7/2/2026'],
      description: 'Calendar day date',
      example: '7/2/2026',
    },
    {
      key: 'fridayOfSmr',
      label: 'Friday Of Smr',
      comment: 'Enter the Friday date associated with the SMR cycle.\n\nExample:\nFriday, July 31, 2026',
      scope: 'job',
      required: false,
      width: 11.5,
      validation: [],
      sampleValues: ['Friday, July 31, 2026'],
      description: 'Friday date of SMR cycle',
      example: 'Friday, July 31, 2026',
    },
    {
      key: 'mondayOfDmr',
      label: 'Monday Of Dmr',
      comment: 'Enter the Monday date associated with the DMR cycle.\n\nExample:\nMonday, July 20, 2026',
      scope: 'job',
      required: false,
      width: 13.8,
      validation: [],
      sampleValues: ['Monday, July 20, 2026'],
      description: 'Monday date of DMR cycle',
      example: 'Monday, July 20, 2026',
    },
    {
      key: 'forumDependency',
      label: 'Forum Dependency',
      comment: 'Enter the date of the forum event this job depends on.\n\nExample:\nThursday, July 23, 2026',
      scope: 'job',
      required: false,
      width: 17.0,
      validation: [],
      sampleValues: ['Thursday, July 23, 2026'],
      description: 'Forum event dependency date',
      example: 'Thursday, July 23, 2026',
    },
    {
      key: 'forumDependencyWorkingDay',
      label: 'Forum Dependency Working Day',
      comment: 'Enter the working day of the forum dependency, relative to the cycle start.\n\nExample:\nWD2',
      scope: 'job',
      required: false,
      width: 28.1,
      validation: [],
      sampleValues: ['WD1', 'WD2', 'WD3'],
      description: 'Working day of forum dependency',
      example: 'WD2',
    },
    {
      key: 'previousJob',
      label: 'Previous Job',
      comment: 'Enter the name of the job that must complete before this one starts.\n\nExample:\nLoad_Previous_Job',
      scope: 'job',
      required: false,
      width: 11.2,
      validation: [],
      sampleValues: ['Load_Previous_Job', 'Data_Validation'],
      description: 'Predecessor job name',
      example: 'Load_Previous_Job',
    },
    {
      key: 'dataProviderForManualFiles',
      label: 'Data Provider For Manual Files',
      comment: 'Identify the provider responsible for supplying manually loaded files for this job.\n\nExample:\nRegional Planning Team',
      scope: 'job',
      required: false,
      width: 25.9,
      validation: [],
      sampleValues: ['Regional Planning Team', 'Local Team'],
      description: 'Provider for manual file loads',
      example: 'Regional Planning Team',
    },
    {
      key: 'itSourceContact',
      label: 'It Source Contact',
      comment: 'Enter the name of the IT contact for this data source.\n\nExample:\nAnastasiia Petrova',
      scope: 'job',
      required: false,
      width: 15.0,
      validation: [],
      sampleValues: ['Anastasiia Petrova', 'IT Support'],
      description: 'IT contact for data source',
      example: 'Anastasiia Petrova',
    },
    {
      key: 'originalExecutionDate',
      label: 'Original Execution Date',
      comment: 'Enter the original date this job was scheduled to execute. This field mirrors the Date Of Execution column.\n\nExample:\nFriday, July 24, 2026',
      scope: 'job',
      required: false,
      width: 20.2,
      validation: [],
      sampleValues: ['Friday, July 24, 2026'],
      description: 'Original scheduled execution date',
      example: 'Friday, July 24, 2026',
    },
    {
      key: 'updatedDate',
      label: 'Updated Date',
      comment: 'Enter the date this record was last updated.\n\nExample:\nFriday, July 24, 2026',
      scope: 'job',
      required: false,
      width: 12.2,
      validation: [],
      sampleValues: ['Friday, July 24, 2026'],
      description: 'Last update date',
      example: 'Friday, July 24, 2026',
    },
    {
      key: 'transferDuration',
      label: 'Transfer Duration',
      comment: 'Enter how long the data transfer for this job typically takes.\n\nExample:\n15 min',
      scope: 'job',
      required: false,
      width: 14.9,
      validation: [],
      sampleValues: ['15 min', '30 min', '1 hour'],
      description: 'Data transfer duration',
      example: '15 min',
    },
    {
      key: 'mdtStartTime',
      label: 'Mdt Start Time',
      comment: 'Enter the start time for this job in Mountain Time (MDT), or indicate Ad Hoc if it does not run on a fixed schedule.\n\nExample:\n01:00:00',
      scope: 'job',
      required: false,
      width: 12.6,
      validation: [],
      sampleValues: ['01:00:00', 'Ad Hoc'],
      description: 'Start time in Mountain Time (MDT)',
      example: '01:00:00',
    },
    {
      key: 'jobNotificationMethod',
      label: 'Job Notification Method',
      comment: 'Specify how notifications for this job are sent.\n\nExample:\nEmail',
      scope: 'job',
      required: false,
      width: 20.6,
      validation: [],
      sampleValues: ['Email', 'Teams', 'Slack'],
      description: 'Notification delivery method',
      example: 'Email',
    },
    {
      key: 'batchLoadStartTime',
      label: 'Batch Load Start Time',
      comment: 'Specify the time when the batch load starts. Enter the value using 24-hour time format (HH:MM:SS).\n\nExample:\n09:30:00',
      scope: 'job',
      required: false,
      width: 18.4,
      validation: [],
      sampleValues: ['09:30:00', '06:00:00'],
      description: 'Batch load start time',
      example: '09:30:00',
    },
    {
      key: 'sourceOwnerContact',
      label: 'Source Owner Contact',
      comment: 'Enter the name of the contact responsible for the source data.\n\nExample:\nMadhusudan Srinivas',
      scope: 'job',
      required: false,
      width: 19.4,
      validation: [],
      sampleValues: ['Madhusudan Srinivas', 'Data Team'],      description: 'Contact for source data ownership',
      example: 'Madhusudan Srinivas',
    },
  ],
  validationRefDataKeys: ['sectors', 'markets', 'reports', 'existingGDONames'],
  sampleRowCount: 3,
  instructionGuide: [
    {
      section: '1. GDO data Structure',
      details: 'File Format',
      description: 'This workbook contains 2 sheets:\n1. Instruction Guide - This sheet with detailed instructions\n2. GDO data - Main data entry sheet\n\nNote: A Summary & Errors sheet is generated automatically after upload for review.',
      example: 'Download template → Fill data → Upload → Review Summary & Errors sheet',
    },
    {
      section: '2. GDO data Sheet (Main Data Entry)',
      details: 'How to Fill',
      description: '• Sheet name must be exactly "GDO data" (case-insensitive) — do not rename it\n• Headers MUST be on row 3 — do not move them up or down\n• Fill data starting from row 4 (row 1 is blank, row 2 is blank, row 3 has headers)\n• Mandatory fields marked with * in header\n• Hover over header cells for field descriptions\n• Do NOT modify the "Remarks / Error Flag" column (locked, grey) — this column shows validation error reasons\n• GDO Name must be unique and start with GDO-<alphanumeric> (e.g., GDO-001, GDO-A1B)\n• Do not delete, reorder, or rename columns\n• Do not insert or delete rows above the data (rows 1-3 are reserved)',
      example: 'GDO-001, GDO-A1B, GDO-ABC — PBNA, BNL, iS&OE, o9, Snowflake, CST, John Doe, ...',
    },
    {
      section: '3. Mandatory Fields (Marked with *)',
      details: 'Required Fields',
      description: 'GDO Name *, Sector *, Market *, Report *, Origin *, Destination *, Timezone *, Owner *, Platform *, Scheduler Name *, Job Name *, Schedule Info *\n\nPlatform-Specific Required Fields (conditionally required, placed after Job Name):\n• ADF Platform → ADF Factory Name * (required when Platform = ADF)\n• ADB Platform → Workspace Name * (required when Platform = ADB)\n• Control-M / Control-M-PGT Scheduler → Application *, Folder Path * (required when Scheduler = Control-M or Control-M-PGT)\n\nNote: These platform-specific fields are used to generate unique Job IDs and query Elasticsearch for job metadata. They are placed immediately after Job Name for logical grouping.',
      example: 'GDO-001, GDO-A1B, GDO-ABC — PBNA, BNL, iS&OE, o9, Snowflake, CST, John Doe, ...\nADF row: ... ADF, ADF, Job1, pepwise-prod-eus2, (empty), (empty), (empty), Mon/09:00/60/Daily\nControl-M row: ... Teradata, Control-M, Job2, (empty), (empty), IBP_PROD, FOLDER/SUBFOLDER, Mon/09:00/60/Daily',
    },
    {
      section: '4. Sector & Market Validation',
      details: 'Independent Validation',
      description: 'Sectors and Markets are loaded from database collections (validated independently).',
      example: 'Sector and Market values must match entries in the database. Case-insensitive.',
    },
    {
      section: '5. Timezone & SLA Fields',
      details: 'Timezone & SLA Format',
      description: 'Timezone: Must be a valid timezone from the list (e.g., CST, EST, PST, UTC, America/New_York, Europe/London, Asia/Tokyo)\nSLA Type: Optional — Daily, Weekly, Monthly, Bi-Weekly, Quarterly\nSLA Days: Optional — "All Days" or comma-separated days (Mon,Tue,Wed...)\nSLA Start/End Time: Optional — HH:MM 24-hr format (CST). Comma-separate multiple times to create multiple SLA slots in one day for cyclic jobs (e.g., 06:00,14:00 paired with 10:00,18:00). Slots pair by position — use the same count in both columns\nSLA End Day Offset: Optional — 0 (same day), 1 (next day), or 2. Required when End Time < Start Time for overnight deadlines\nSLA Max Runtime (min): Optional — Explicit max runtime in minutes for INTERVAL SLA. Takes precedence over Start/End Time calculation. Comma-separate one value per SLA slot (e.g., 30,45,60)\nSLA Criteria: Optional — ANY RUN (at least one run must complete in the window), ALL RUNS (every run in the window must complete), SELECTED RUNS (only the selected SLA slots are evaluated), LATEST RUN (latest run of the day; deadline may roll into the next day)\nSLA Schedule Trigger: Optional — Scheduled (default) or Triggered (event-driven)',
      example: 'Timezone: CST, SLA Type: Daily, SLA Days: Mon,Tue,Wed,Thu,Fri, SLA Start: 08:00, SLA End: 18:00, Criteria: ANY RUN\nOvernight SLA: SLA Start: 22:00, SLA End: 06:00, SLA End Day Offset: 1, Criteria: LATEST RUN\nMultiple SLA slots (cyclic job): SLA Start: 06:00,14:00, SLA End: 10:00,18:00, Criteria: ALL RUNS\nDuration-based: SLA Type: Weekly, SLA Max Runtime: 120 (or 30,45,60 for three slots), Criteria: ALL RUNS',
    },
    {
      section: '6. Optional Fields',
      details: 'Non-Mandatory Fields',
      description: 'GDO Description/Label, GDO Dependent On, Stage Inbound/Outbound, Data Owner, Data Steward',
      example: 'Stage Inbound/Outbound: Inbound, Outbound, Both\nGDO Dependent On: comma-separated GDO names (e.g., GDO-001,GDO-A1B). Use NONE to clear all dependencies.',
    },
    {
      section: '7. Remarks / Error Flag Column',
      details: 'Error Messages',
      description: 'The Remarks / Error Flag column is the first column in the invalid entries template.\n• Shows validation error reasons (e.g., "Duplicate GDO Name", "Invalid market", "Field is required")\n• Grey, locked, system-populated — do not edit\n• When re-uploading, this column is automatically ignored by the parser\n• Use this column to understand what went wrong and fix the data',
      example: 'Error: "Duplicate GDO Name" → Change GDO Name to be unique\nError: "Invalid market" → Use a valid market from the Markets collection',
    },
    {
      section: '8. After Upload - Summary & Errors Sheet',
      details: 'Post-Upload Review',
      description: 'After clicking "Confirm and Upload", a new sheet "Summary & Errors" is generated with:\n• ✅ Successfully Uploaded - Green: Successfully created GDOs\n• ❌ Not Uploaded (Errors) - Red: Failed validations with reasons\n• ⏭ Skipped (Deselected) - Amber: Valid but unchecked rows\n• Download Invalid Entries Template - Orange button to download failed rows as template for correction',
      example: 'Review the "Not Uploaded (Errors)" section, fix issues, download template, correct, re-upload',
    },
    {
      section: '9. Error Resolution Workflow',
      details: 'Fixing Errors',
      description: '1. Review "Not Uploaded (Errors)" rows in Summary sheet\n2. Click "Download Invalid Entries Template" to get pre-filled template with error reasons in Remarks column\n3. Fix the data in the template (errors shown in Remarks column)\n4. Re-upload the corrected file\n4. Repeat until all rows upload successfully',
      example: 'Error: "Duplicate GDO Name" → Change GDO Name\nError: "Wrong input string" for Market → Use valid market for sector',
    },
    {
      section: '10. Validation Rules Summary',
      details: 'Key Rules',
      description: 'File structure (do NOT alter):\n• Sheet name must be exactly "GDO data"\n• Headers must be on row 3 (rows 1-2 are blank)\n• Data starts on row 4\n\nField rules:\n• GDO Name: Must start with GDO-<alphanumeric> (e.g., GDO-001, GDO-A1B), max 64 chars, unique\n• Sector: Must match a value from the Sectors collection\n• Market: Must match a value from the Markets collection\n• Report: Must match a value from the Reports collection\n• Origin, Destination, Platform, Scheduler Name, Job Name: Required, any value\n• Timezone: Must be a valid timezone\n• Schedule Days: Full day names (Monday, Tuesday, etc.), comma-separated, or All\n• Execution Start Time: 24-hr HH:MM (e.g., 06:00, 14:30)\n• At least 2 of Start Time / End Time / Avg Execution Time must be provided\n• Remarks column: Shows error reasons (system-only, ignored on re-upload)\n\nPlatform-Specific Required Fields (conditionally required when ENABLE_PLATFORM_VALIDATION = true):\n• ADF Platform → ADF Factory Name\n• ADB Platform → Workspace Name\n• Control-M / Control-M-PGT Scheduler → Application + Folder Path\n\nJob ID Generation Logic (DISABLED mode — uses jobName directly):\n• All platforms: jobName used as Job_Id (no ES sync, no hashing)',
      example: 'Valid sectors, markets, and reports are loaded from the database.\nADF rows need: ADF Factory Name (e.g., pepwise-prod-eus2)\nADB rows need: Workspace Name (e.g., pepwise-prod-workspace)\nControl-M rows need: Application + Folder Path (e.g., IBP_PROD + FOLDER/SUBFOLDER)\nJob IDs are generated deterministically and will be overwritten when platform sync runs.',
    },
    {
      section: '11. Re-upload After Corrections',
      details: 'Re-Upload',
      description: '1. Download "Invalid Entries Template" from Summary sheet\n2. Fix errors in the downloaded file (Remarks column shows errors)\n3. Keep all columns intact\n4. Upload corrected file using "Bulk Upload"\n5. Repeat until 0 errors',
      example: 'Download → Fix → Upload → Verify → Repeat if needed',
    },
    {
      section: '12. Support & Troubleshooting',
      details: 'Common Issues',
      description: 'File structure issues:\n• "Sheet not found" or parse error: Ensure the sheet is named exactly "GDO data" (not "Sheet1", not "GDO Data" with different casing)\n• Headers not recognized: Ensure headers are on row 3 (not row 1 or 2). Rows 1-2 must be blank.\n• Data not appearing in preview: Ensure data starts on row 4 (row 3 must have headers)\n\nField validation issues:\n• "GDO name should start with GDO-<alphanumeric>": Fix prefix to be GDO- followed by alphanumeric characters (e.g., GDO-001, GDO-A1B)\n• "Field is required": Fill in the mandatory field\n• "Schedule Days" validation error: Use full day names (Monday, Tuesday, etc.), not abbreviations\n• "Execution Start Time" validation error: Use 24-hour HH:MM format (e.g., 06:00, 14:30)\n• Platform must be entered as "ADF" (not "Azure Data Factory")\n\nNote: Do not rename the sheet, move headers, or insert/delete rows above the data.',
      example: 'Error: "Field is required" → Fill in the missing mandatory field\nError: "ADF Factory Name is required" → Enter the ADF instance name (e.g., pepwise-prod-eus2)\nError: "Workspace Name is required" → Enter the Databricks workspace name (e.g., pepwise-prod-workspace)\nError: "Application is required" → Enter the Control-M application name (e.g., IBP_PROD)',
    },
  ],
};

/**
 * Get column definition by key
 */
export function getColumnByKey(key: string): ColumnDefinition | undefined {
  return IBP_TEMPLATE.columns.find(col => col.key === key);
}

/**
 * Get all required columns
 */
export function getRequiredColumns(): ColumnDefinition[] {
  return IBP_TEMPLATE.columns.filter(col => col.required);
}

/**
 * Get columns by scope
 */
export function getColumnsByScope(scope: ColumnScope): ColumnDefinition[] {
  return IBP_TEMPLATE.columns.filter(col => col.scope === scope);
}

/**
 * Get column keys array (for Excel headers)
 */
export function getColumnKeys(): string[] {
  return IBP_TEMPLATE.columns.map(col => col.key);
}

/**
 * Get column labels array (for Excel headers)
 */
export function getColumnLabels(): string[] {
  return IBP_TEMPLATE.columns.map(col => col.label);
}

/**
 * Get column widths array (for Excel)
 */
export function getColumnWidths(): { wch: number }[] {
  return IBP_TEMPLATE.columns.map(col => ({ wch: col.width }));
}

/**
 * Generate dynamic comment for a column
 */
export function getColumnComment(key: string, refData?: any): string {
  const col = getColumnByKey(key);
  if (!col) return '';
  
  if (typeof col.comment === 'function') {
    return col.comment(refData);
  }
  return col.comment;
}

/**
 * Get sample values for template generation
 */
export function getSampleValues(key: string): string[] {
  const col = getColumnByKey(key);
  return col?.sampleValues || [];
}

/**
 * Create a template configuration for a different business stream
 */
export function createBusinessStreamTemplate(
  businessStream: string,
  columns: ColumnDefinition[],
  options?: Partial<TemplateConfig>
): TemplateConfig {
  return {
    id: `${businessStream.toLowerCase()}-bulk-upload`,
    name: `${businessStream} Bulk Upload Template`,
    description: `Template for bulk uploading data for ${businessStream} business stream`,
    businessStream,
    fileName: `${businessStream}_Bulk_Upload_Template.xlsx`,
    sheetName: `${businessStream} data`,
    columns,
    validationRefDataKeys: ['sectors', 'markets', 'reports', 'existingGDONames'],
    sampleRowCount: 3,
    instructionGuide: IBP_TEMPLATE.instructionGuide,
    ...options,
  };
}

/**
 * Merge template configurations
 */
export function mergeTemplateConfigurations(
  base: TemplateConfig,
  overrides: Partial<TemplateConfig>
): TemplateConfig {
  return {
    ...base,
    ...overrides,
    columns: overrides.columns || base.columns,
    instructionGuide: overrides.instructionGuide || base.instructionGuide,
  };
}

/**
 * Validate a row against template configuration
 */
export function validateRowAgainstTemplate(
  row: Record<string, any>,
  template: TemplateConfig,
  refData?: any
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const column of template.columns) {
    const value = row[column.key];
    const columnErrors = validateColumn(value, column, row, refData);
    if (columnErrors) {
      errors[column.key] = columnErrors;
    }
  }

  return errors;
}

/**
 * Validate a single column value
 */
function validateColumn(
  value: any,
  column: ColumnDefinition,
  row: Record<string, any>,
  refData?: any
): string | null {
  const stringValue = String(value || '').trim();

  // Check required
  if (column.required && !stringValue) {
    return 'Field is required';
  }

  // Check validation rules
  for (const rule of column.validation) {
    const error = validateRule(stringValue, rule, row, refData);
    if (error) {
      return error;
    }
  }

  return null;
}

/**
 * Validate a single validation rule
 */
function validateRule(
  value: string,
  rule: ValidationRule,
  row: Record<string, any>,
  refData?: any
): string | null {
  // Skip validation for empty optional fields, EXCEPT for custom validators
  // that may need to check conditional required logic (e.g., application/folderPath
  // required when scheduler is Control-M)
  if (!value && rule.type !== 'required' && rule.type !== 'custom') {
    return null; // Skip validation for empty optional fields
  }

  switch (rule.type) {
    case 'required':
      return !value ? rule.message : null;

    case 'pattern':
      return rule.pattern && !rule.pattern.test(value) ? rule.message : null;

    case 'enum':
      if (!rule.allowedValues) return null;
      const allowed = Array.isArray(rule.allowedValues) 
        ? rule.allowedValues 
        : rule.allowedValues(refData);
      return !allowed.includes(value) ? rule.message : null;

    case 'reference':
      if (!refData || !rule.referenceType) return null;
      const refValues = refData[rule.referenceType];
      if (!refValues) return null;
      const found = refValues.some((v: string) => v.toLowerCase() === value.toLowerCase());
      return !found ? rule.message : null;

    case 'custom':
      return rule.customValidator ? rule.customValidator(value, row, refData) : null;

    case 'dependent':
      // Dependent validation is handled at row level
      return null;

    default:
      return null;
  }
}
